/**
 * SettingsNotificationsPage — Landlord
 * Route: /settings/notifications
 * Allows the user to enable or disable push notifications and manage
 * device registration.
 */

import { useEffect, useState } from 'react';
import type { CategoryPreferences, NotificationTypeDefinition } from '@resida/notification-types';
import { listSuppressibleCategories, resolveChannels } from '@resida/notification-types';
import { SettingsLayout } from '../components/settings/SettingsLayout';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { getNotificationPreferences, updateNotificationPreferences } from '../lib/api';

/**
 * Human-readable heading for a category that groups more than one
 * suppressible registry entry (e.g. `payments_banking` -> "Payments Banking").
 * Categories with a single entry use that entry's own label instead.
 */
function humanizeCategoryName(category: string): string {
  return category
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function SettingsNotificationsPage() {
  // AuthProvider's instance owns foreground-message display for the app session.
  const { permissionState, isRegistered, requestPermissionAndRegister, deregister } = usePushNotifications({
    handleForegroundMessages: false,
  });
  const [pushEnabled, setPushEnabled] = useState<boolean | null>(null);
  const [emailEnabled, setEmailEnabled] = useState<boolean | null>(null);
  const [categoryPreferences, setCategoryPreferences] = useState<CategoryPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [isEnablingThisDevice, setIsEnablingThisDevice] = useState(false);

  useEffect(() => {
    getNotificationPreferences()
      .then(({ preferences }) => {
        setPushEnabled(preferences.pushEnabled);
        setEmailEnabled(preferences.emailEnabled);
        setCategoryPreferences(preferences.categoryPreferences);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load notification preferences');
      })
      .finally(() => setIsLoading(false));
  }, []);

  const handleToggle = async (checked: boolean) => {
    setPushEnabled(checked);
    setError(null);
    try {
      await updateNotificationPreferences({ pushEnabled: checked });
      if (checked && !isRegistered) {
        await requestPermissionAndRegister();
      } else if (!checked && isRegistered) {
        await deregister();
      }
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update notification preferences');
    }
  };

  // pushEnabled is an account-wide preference (can be true from another
  // device, or the manager default) and is intentionally independent of
  // isRegistered, which reflects only THIS browser's own FCM registration —
  // see settings-notifications.e2e.spec.ts's push-toggle test, which asserts
  // that contract. A manager can therefore see the toggle checked while this
  // specific browser was never set up to actually receive anything; surface
  // that gap explicitly instead of leaving it silent.
  const handleEnableOnThisDevice = async () => {
    setError(null);
    setIsEnablingThisDevice(true);
    try {
      await requestPermissionAndRegister();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enable push notifications on this device');
    } finally {
      setIsEnablingThisDevice(false);
    }
  };

  const handleEmailToggle = async (checked: boolean) => {
    setEmailEnabled(checked);
    setError(null);
    try {
      await updateNotificationPreferences({ emailEnabled: checked });
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update notification preferences');
    }
  };

  const handleCategoryToggle = async (category: string, channel: 'email' | 'push', checked: boolean) => {
    const next: CategoryPreferences = {
      ...categoryPreferences,
      [category]: { ...categoryPreferences?.[category], [channel]: checked },
    };
    setCategoryPreferences(next);
    setError(null);
    try {
      await updateNotificationPreferences({ categoryPreferences: next });
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update notification preferences');
    }
  };

  return (
    <SettingsLayout>
      <section
        data-testid="notifications-page"
        className="px-[var(--page-padding-x)] flex flex-col gap-6"
      >
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-1">Push Notifications</h2>
          <p id="push-notif-description" className="text-sm text-muted-foreground">
            Receive push notifications for new messages, even when the app is closed.
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="p-3 bg-destructive/10 border border-destructive rounded text-sm text-destructive"
            data-testid="notifications-error"
          >
            {error}
          </div>
        )}

        {permissionState === 'denied' && (
          <p
            role="alert"
            data-testid="notif-permission-denied-msg"
            className="text-sm text-destructive"
          >
            Push notifications are blocked. Check your browser&apos;s site permissions for this page,
            and — on macOS — also check System Settings → Notifications to make sure your browser (or
            this installed app) is allowed to show notifications.
          </p>
        )}

        {permissionState !== 'denied' && !isLoading && pushEnabled !== null && (
          <label
            data-testid="push-notifications-toggle-label"
            className="flex items-center gap-3 cursor-pointer"
          >
            <input
              type="checkbox"
              data-testid="push-notifications-toggle"
              aria-describedby="push-notif-description"
              checked={!!pushEnabled}
              onChange={(e) => handleToggle(e.target.checked)}
              className="w-4 h-4 accent-primary"
            />
            <span className="text-sm text-foreground">Enable push notifications</span>
          </label>
        )}

        {permissionState !== 'denied' && permissionState !== 'unsupported' && pushEnabled === true && !isRegistered && (
          <div
            data-testid="push-not-registered-notice"
            role="status"
            className="flex items-center justify-between gap-3 p-3 bg-warning/10 border border-warning/20 rounded-md text-sm"
          >
            <span className="text-foreground">
              Push notifications are enabled for your account, but this browser hasn&apos;t been set up to
              receive them yet.
            </span>
            <button
              type="button"
              data-testid="enable-push-this-device-button"
              onClick={handleEnableOnThisDevice}
              disabled={isEnablingThisDevice}
              className="shrink-0 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:cursor-not-allowed disabled:opacity-50 px-3 py-1.5 text-sm font-medium"
            >
              {isEnablingThisDevice ? 'Enabling…' : 'Enable on this device'}
            </button>
          </div>
        )}

        <div>
          <h2 className="text-lg font-semibold text-foreground mb-1">Email Notifications</h2>
          <p id="email-notif-description" className="text-sm text-muted-foreground">
            Receive email notifications for new messages and account activity.
          </p>
        </div>

        {!isLoading && emailEnabled !== null && (
          <label
            data-testid="email-notifications-toggle-label"
            className="flex items-center gap-3 cursor-pointer"
          >
            <input
              type="checkbox"
              data-testid="email-notifications-toggle"
              aria-describedby="email-notif-description"
              checked={!!emailEnabled}
              onChange={(e) => handleEmailToggle(e.target.checked)}
              className="w-4 h-4 accent-primary"
            />
            <span className="text-sm text-foreground">Enable email notifications</span>
          </label>
        )}

        {!isLoading && categoryPreferences !== null && (
          <div className="flex flex-col gap-4" data-testid="notification-categories-section">
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-1">Notification Categories</h2>
              <p className="text-sm text-muted-foreground">
                Choose which notification categories you receive by email and push. In-app
                notifications are always delivered.
              </p>
            </div>

            {listSuppressibleCategories().map(({ category, entries }) => {
              // categoryPreferences persists (and the BR-68 orchestrator enforces) exactly one
              // email/push state per CATEGORY, not per registry entry (BR-71). Render exactly one
              // row per category, matching what a toggle here actually controls — never one row
              // per entry, which would falsely imply independent per-entry control for categories
              // like payments_banking that contain more than one suppressible entry.
              const isGrouped = entries.length > 1;
              // listSuppressibleCategories() only ever produces a category group from at least
              // one entry, so `entries[0]` is always present for the single-entry case.
              const firstEntry = entries[0] as NotificationTypeDefinition;
              const heading = isGrouped ? humanizeCategoryName(category) : firstEntry.label;
              const description = isGrouped
                ? `Applies to: ${entries.map((entry) => entry.label).join(', ')}.`
                : firstEntry.description;
              const availableChannels = (['email', 'push'] as const).filter((channel) =>
                entries.some((entry) => resolveChannels(entry).includes(channel))
              );

              return (
                <fieldset
                  key={category}
                  data-testid={`category-${category}-row`}
                  className="flex flex-col gap-2 pb-4 border-0 border-b border-border last:border-0 last:pb-0 p-0 m-0"
                >
                  <legend className="p-0">
                    <h3 className="text-sm font-medium text-foreground">{heading}</h3>
                  </legend>
                  <p className="text-xs text-muted-foreground">{description}</p>
                  {isGrouped && (
                    <ul className="text-xs text-muted-foreground list-disc pl-4">
                      {entries.map((entry) => (
                        <li key={entry.type}>{entry.description}</li>
                      ))}
                    </ul>
                  )}
                  <div className="flex items-center gap-6">
                    {availableChannels.map((channel) => (
                      <label
                        key={channel}
                        data-testid={`category-${category}-${channel}-toggle-label`}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          data-testid={`category-${category}-${channel}-toggle`}
                          checked={categoryPreferences?.[category]?.[channel] ?? true}
                          onChange={(e) => handleCategoryToggle(category, channel, e.target.checked)}
                          className="w-4 h-4 accent-primary"
                        />
                        <span className="text-sm text-foreground capitalize">{channel}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              );
            })}
          </div>
        )}

        {savedAt !== null && (
          <div
            data-testid="notifications-saved-msg"
            className="rounded-lg border border-success/30 bg-success/5 p-3"
            role="status"
          >
            <p className="text-sm text-success m-0">Saved</p>
          </div>
        )}

        {isLoading && (
          <div className="flex items-center gap-2" aria-label="Loading preferences">
            <div
              className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"
              aria-hidden="true"
            />
            <p className="text-sm text-muted-foreground">Loading preferences...</p>
          </div>
        )}
      </section>
    </SettingsLayout>
  );
}
